import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import type { Utente } from 'api'
import { compact } from 'lodash-es'
import {
  EyeIcon,
  EyeOffIcon,
  KeyIcon,
  MoreHorizontalIcon,
  ShieldBanIcon,
  ShieldCheckIcon,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { cn } from '@/lib/utils.ts'
import { useDexieQuery } from '@/hooks/use-dexie-query'
import { utentiPage } from '@/db/local/user-lists'
import { useModalState } from '@/hooks/use-modal-state'
import { usePagination } from '@/hooks/use-pagination'
import { useSyncManager } from '@/hooks/use-sync-manager'
import { AdminOnly } from '@/components/admin-only'
import { Badge } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { confirm } from '@/components/confirmation'
import { DataTable } from '@/components/data-table/data-table'
import { PageTitle } from '@/components/layout/page-title'
import { trpc } from '@/clients/trpc.ts'
import { useMyRole } from '@/features/auth/hooks/use-my-role'
import { ChangePasswordDialog } from '@/features/auth/components/change-password-dialog'
import {
  generateSecurePassword,
  getFieldErrors,
} from '@/features/auth/utils/password-utils'

export const Route = createFileRoute('/_authenticated/impostazioni/utenti')({
  component: () => (
    <AdminOnly>
      <RouteComponent />
    </AdminOnly>
  ),
  validateSearch: z
    .object({
      ricerca: z.coerce.string().optional(),
      pagina: z.coerce.number().optional(),
      perPagina: z.coerce.number().optional(),
    })
    .optional(),
})

interface CreateUtenteInput {
  username: string
  first_name: string
  last_name: string
  role: 'admin' | 'operatore'
  password: string
}

function AddUtenteDialog() {
  const [open, setOpen] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const form = useForm<CreateUtenteInput>({
    defaultValues: {
      username: '',
      first_name: '',
      last_name: '',
      role: 'operatore',
      password: '',
    },
  })
  const { syncOne } = useSyncManager()

  const mutation = useMutation({
    mutationFn: (values: CreateUtenteInput) =>
      trpc.auth.createUser.mutate(values),
    onSuccess: async () => {
      syncOne('utenti')
      toast.success('Utente creato')
      setOpen(false)
      form.reset()
      setShowPassword(false)
    },
    onError: (error: unknown) => {
      const fieldErrors = getFieldErrors(error)
      if (fieldErrors) {
        Object.entries(fieldErrors).forEach(([key, messages]) => {
          if (messages?.length) {
            // @ts-expect-error react-hook-form field type
            form.setError(key, { type: 'server', message: messages.join(', ') })
          }
        })
        return
      }

      toast.error((error as any)?.message ?? 'Si è verificato un errore')
    },
  })

  const onSubmit = () => mutation.mutateAsync(form.getValues())

  const handleDialogOpenChange = (open: boolean) => {
    setOpen(open)
    if (!open) {
      // Reset form and password visibility when dialog closes
      form.reset()
      setShowPassword(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogTrigger asChild>
        <Button>Nuovo Utente</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuovo Utente</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <TooltipProvider>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            {...field}
                            className="pr-10"
                          />
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex={-1}
                              >
                                {showPassword ? (
                                  <EyeOffIcon className="h-4 w-4" />
                                ) : (
                                  <EyeIcon className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                {showPassword
                                  ? 'Nascondi password'
                                  : 'Mostra password'}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={async () => {
                                const newPassword = generateSecurePassword()
                                field.onChange(newPassword)
                                setShowPassword(true)

                                // Copy to clipboard
                                try {
                                  await navigator.clipboard.writeText(
                                    newPassword
                                  )
                                  toast.success(
                                    'Password generata e copiata negli appunti!'
                                  )
                                } catch (error) {
                                  console.error(
                                    'Failed to copy password to clipboard:',
                                    error
                                  )
                                  toast.success('Password memorabile generata!')
                                }
                              }}
                            >
                              <KeyIcon className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Genera password e copia negli appunti</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TooltipProvider>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cognome</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ruolo</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona ruolo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="operatore">Operatore</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Annulla
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                Crea
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function ToggleUserMenuItem({ user }: { user: Utente }) {
  const { syncOne } = useSyncManager()
  const mutation = useMutation({
    mutationFn: (disabled: boolean) =>
      trpc.auth.toggleUser.mutate({ user_id: user.user_id, disabled }),
    onSuccess: async () => {
      syncOne('utenti')
      toast.success('Utente aggiornato')
    },
    onError: () => {
      toast.error("Errore durante l'aggiornamento")
    },
  })

  const handleClick = () => {
    confirm({
      title: user.deactivated_at ? 'Abilita utente' : 'Disabilita utente',
      content: user.deactivated_at
        ? `Sei sicuro di voler abilitare ${user.username}?`
        : `Sei sicuro di voler disabilitare ${user.username}?`,
      onOk: () => mutation.mutateAsync(!user.deactivated_at),
    })
  }

  return (
    <DropdownMenuItem onSelect={handleClick} disabled={mutation.isPending}>
      {user.deactivated_at ? (
        <>
          <ShieldCheckIcon className="mr-2 h-4 w-4" /> Abilita utente
        </>
      ) : (
        <>
          <ShieldBanIcon className="mr-2 h-4 w-4" /> Disabilita utente
        </>
      )}
    </DropdownMenuItem>
  )
}

function UtenteActionsMenu({ utente }: { utente: Utente }) {
  const {
    isOpen: isPasswordDialogOpen,
    open: openPasswordDialog,
    setOpen: setPasswordDialogOpen,
  } = useModalState(false)

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
            <MoreHorizontalIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onSelect={() => openPasswordDialog()}>
            <KeyIcon className="mr-2 h-4 w-4" /> Cambia password
          </DropdownMenuItem>
          <ToggleUserMenuItem user={utente} />
        </DropdownMenuContent>
      </DropdownMenu>
      <ChangePasswordDialog
        variant="admin"
        username={utente.username}
        syncKey="utenti"
        onPasswordChange={(password) =>
          trpc.auth.changeUserPassword.mutate({
            user_id: utente.user_id,
            password,
          })
        }
        open={isPasswordDialogOpen}
        onOpenChange={(nextOpen) => setPasswordDialogOpen(nextOpen)}
      />
    </>
  )
}

function RouteComponent() {
  const searchParams = Route.useSearch()
  const navigate = Route.useNavigate()
  const role = useMyRole()
  const { pagina = 1, perPagina = 20 } = searchParams ?? {}
  const { skip, limit } = usePagination(pagina as number, perPagina as number)
  const handlePaginationChange = (pagination: PaginationState) => {
    navigate({
      search: (s) => ({
        ...s,
        pagina: pagination.pageIndex + 1,
        perPagina: pagination.pageSize,
      }),
    })
  }

  const columns: Array<ColumnDef<Utente>> = useMemo(
    () =>
      compact([
        {
          accessorKey: 'username',
          header: 'Username',
        },
        {
          accessorKey: 'first_name',
          header: 'Nome',
        },
        {
          accessorKey: 'last_name',
          header: 'Cognome',
        },
        {
          accessorKey: 'deactivated_at',
          header: 'Stato',
          cell: ({ row }) => (
            <Badge
              className={cn(
                row.original.deactivated_at
                  ? 'border-red-300 bg-red-200 text-red-800'
                  : 'border-green-300 bg-green-200 text-green-800'
              )}
              variant="outline"
            >
              {row.original.deactivated_at ? 'Disabilitato' : 'Attivo'}
            </Badge>
          ),
        },
        role === 'admin'
          ? {
              id: 'actions',
              cell: ({ row }) => (
                <div className="flex justify-end">
                  <UtenteActionsMenu utente={row.original} />
                </div>
              ),
            }
          : undefined,
      ]),
    [role]
  )

  const utenti = useDexieQuery((db) => utentiPage(db, skip, limit))
  const utentiCount = useDexieQuery((db) => db.utenti.count())

  return (
    <>
      <Breadcrumbs
        items={[
          { name: 'Impostazioni' },
          { name: 'Utenti', to: '/impostazioni/utenti' },
        ]}
      />

      <PageTitle className="flex items-center justify-between">
        <span>Utenti</span>
        {role === 'admin' && <AddUtenteDialog />}
      </PageTitle>

      <DataTable
        columns={columns}
        data={utenti ?? []}
        getRowId={(row) => row.user_id}
        count={utentiCount}
        initialPage={pagina as number}
        pageSize={perPagina as number}
        onPaginationChange={handlePaginationChange}
      />
    </>
  )
}
